import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { UserPermissionsEnum } from '../../../core/enums/user-permissions.enum';
import { ManagedUserPermissionsForm } from '../../../core/forms/managed-user.form';
import { UserPermissionOptionsInterface, UserPermissionsInterface } from '../../../core/models/user-permissions.model';
import { ERROR_STATUSES } from '../../../header-message/server-errors.interceptor';
import { SharedModule } from '../../../shared/shared.module';
import { ToastService } from '../../../ui-elements/toast/toast.service';
import { ManagedUserProfileComponent } from '../../components/managed-user-profile/managed-user-profile.component';
import { UserPermissionsComponent } from '../../components/user-permissions/user-permissions.component';
import { ManagedUsersService } from '../../services/managed-users.service';

@Component({
  selector: 'app-add-managed-user-modal',
  imports: [SharedModule, ManagedUserProfileComponent, UserPermissionsComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './add-managed-user-modal.component.html',
})
export class AddManagedUserModalComponent implements OnInit {
  private managedUsersService = inject(ManagedUsersService);
  private translator = inject(TranslateService);
  private toastService = inject(ToastService);
  private activeModal = inject(NgbActiveModal);

  @Input() permissionOptions: UserPermissionOptionsInterface;
  @Output() addedUser = new EventEmitter<void>();

  activeTab: 'information' | 'permissions' = 'information';
  profileForm = this.managedUsersService.getUserProfileForm();
  permissionsForm: ManagedUserPermissionsForm;
  savingInProgress = false;

  ngOnInit(): void {
    const defaultUserPermissions: UserPermissionsInterface = {
      granted: this.permissionOptions.granted
        .filter((permission) => permission.default && !permission.disabled)
        .map((permission) => permission.value) as UserPermissionsEnum[],
      deniedInformationPages: this.permissionOptions.informationPages
        .filter((permission) => !permission.default || permission.disabled)
        .map((permission) => permission.value),
      deniedNewsCategories: [],
    };

    this.permissionsForm = this.managedUsersService.getUserPermissionsForm(defaultUserPermissions, this.permissionOptions.informationPages);
  }

  onSetActiveTabToPermissions(): void {
    this.profileForm.markAllAsTouched();
    this.profileForm.updateValueAndValidity();

    if (this.profileForm.valid) {
      this.activeTab = 'permissions';
    }
  }

  onAddUser(): void {
    if (this.savingInProgress) {
      return;
    }

    this.savingInProgress = true;

    const formattedPermissionsForUpdate = this.managedUsersService.formatPermissionsForUpdate(
      this.permissionsForm.getRawValue(),
      this.permissionOptions,
    );
    const userData = {
      ...this.profileForm.getRawValue(),
      permissions: formattedPermissionsForUpdate,
    };

    this.managedUsersService
      .create(userData)
      .pipe(
        finalize(() => {
          this.savingInProgress = false;
        }),
      )
      .subscribe({
        next: () => {
          this.addedUser.emit();
          this.activeModal.dismiss();
        },
        error: (error) => {
          const permissionsErrors = error.error?.errors?.children?.permissions?.children;

          if (error instanceof HttpErrorResponse && error.status === ERROR_STATUSES.BAD_REQUEST && error.error?.error === 'ER_100001#009') {
            this.toastService.danger(this.translator.instant('ERRORS.BY_ERROR_CODE.ER_100001#009'));
          } else if (permissionsErrors?.granted?.errors || permissionsErrors?.informationPages?.errors) {
            this.toastService.danger(this.translator.instant('MANAGED_USERS.PERMISSIONS_NO_LONGER_AVAILABLE'));
            this.managedUsersService.getPermissionOptions().subscribe((permissionOptions) => {
              this.permissionOptions = permissionOptions;
            });
          }
        },
      });
  }

  get informationTabActive(): boolean {
    return this.activeTab === 'information';
  }
}
